import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { includes } from 'lodash';
import isEqual from 'lodash/isEqual';
import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  ButtonGroup,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  ToggleButton,
} from 'react-bootstrap';
import { BsGrid3X3GapFill, BsInfoCircle, BsJustify } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  AssignmentsService,
  ConnectedFacility,
  ConnectedFacilityService,
  OpenAPI,
  ProvidersService,
  UserTypeEnum,
} from '../api';
import { Card } from '../app/components/Card';
import { useConfirmationModal } from '../app/components/ConfirmationModal';
import { ConnectionRequestCard } from '../app/components/ConnectionRequestCard';
import { RejectConnectionDialog } from '../app/components/ConnectionRequestCard/RejectConnectionDialog';
import { ReviewConnectionDialog } from '../app/components/ConnectionRequestCard/ReviewConnectionDialog';
import {
  ConnectionRequestsFilters,
  defaultFilter,
} from '../app/components/ConnectionRequestsFilters';
import { ConnectionRequestsTable } from '../app/components/ConnectionRequestsTable';
import { ConnectionStatusCategoryEnum } from '../app/components/ConnectionRequestsTable/ConnectionStatusCategoryEnum';
import { FacilityCellDetailsPortal } from '../app/components/ConnectionRequestsTable/FacilityCellDetails';
import { useFillQuestionnaireDialog } from '../app/components/FillQuestionnaireDialog';
import { ListFooter } from '../app/components/ListFooter';
import {
  PendingConnectionRequestsBadge,
  PendingPresentationRequestsBadge,
} from '../app/components/NotificationBadge';
import { ProfileIncompleteCard } from '../app/components/ProfileIncompleteCard';
import { ReviewAvailability } from '../app/components/ReviewAvailability';
import { TabsNavbar } from '../app/components/TabsNavbar';
import { getConnectionsPageSearchPlaceholder } from '../app/constants/healthcare-entity';
import { BUTTON_CLASS_NAME, BUTTON_STYLES } from '../app/constants/view-switcher';
import { SessionContext } from '../app/contexts/SessionContext';
import { useCurrentUser } from '../app/hooks/auth';
import { SIZE_LG, useBreakpoint } from '../app/hooks/breakpoint';
import { useSearchParamsState } from '../app/hooks/search-params';
import { useDocumentTitle } from '../app/hooks/use-document-title';
import { useUserProgress } from '../app/hooks/user-progress';
import { SortDataPayload } from '../app/models/sort';
import { connectionRequestsQueryKeys } from '../app/query-keys-factories/connection-requests';
import { healthcareEntityFacilityQueryKeys } from '../app/query-keys-factories/facilities';
import { userQueryKeys } from '../app/query-keys-factories/user';
import { handleApiError } from '../app/utils/errorhandling';
import { useConnectionRequestsFilters } from './hooks/useConnectionRequestsFilters';

const ConnectionRequestsEmptyInfoMessage = () => (
  <Row className="m-1">
    <div className="empty-list-info-message">
      <p>
        <BsInfoCircle className="tw:inline me-2 mb-1" />
        This page is where you can review all of your connection requests and relevant details,
        including your rates for each connection.
      </p>
      <p>
        When your profile matches a potential opportunity, you will receive an email notification
        with more information. Rest assured, your personal information will remain private until you
        decide whether you are interested in pursuing the opportunity.
      </p>
    </div>
  </Row>
);

// eslint-disable-next-line sonarjs/cognitive-complexity
export const ConnectionRequestsList = () => {
  useDocumentTitle('Connections');
  const [isGridView, setIsGridView] = useState(true);
  const [showReviewAvailabilityConfirmation, setShowReviewAvailabilityConfirmation] =
    useState(false);
  const [reviewedConnectionRequest, setReviewedConnectionRequest] =
    useState<ConnectedFacility | null>(null);
  const [rejectedConnectionRequest, setRejectedConnectionRequest] =
    useState<ConnectedFacility | null>(null);
  const [page, setPage] = useSearchParamsState('page', '1');
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractText, setContractText] = useState<string | null>(null);
  const [isLoadingContract, setIsLoadingContract] = useState(false);
  const [isSigningContract, setIsSigningContract] = useState(false);
  const [isDownloadingContract, setIsDownloadingContract] = useState(false);
  const [signedFirstName, setSignedFirstName] = useState('');
  const [signedLastName, setSignedLastName] = useState('');

  const { user } = useCurrentUser();
  const userIsProvider = user?.user_type === UserTypeEnum.PROVIDER;
  const { notifications } = useContext(SessionContext) as {
    notifications?: {
      isLoading: boolean;
      notifications?: Record<string, number>;
    };
  };

  const initialStatusCategory = useMemo(() => {
    if (notifications?.isLoading) {
      return ConnectionStatusCategoryEnum.COMPLETED;
    }

    const pendingCount = notifications?.notifications?.['connection_requests'] ?? 0;
    const inProcessCount = notifications?.notifications?.['connection_responded_requests'] ?? 0;

    if (userIsProvider) {
      if (pendingCount > 0) return ConnectionStatusCategoryEnum.PENDING_RESPONSE;
      if (inProcessCount > 0) return ConnectionStatusCategoryEnum.IN_PROCESS;
    } else {
      if (inProcessCount > 0) return ConnectionStatusCategoryEnum.IN_PROCESS;
      if (pendingCount > 0) return ConnectionStatusCategoryEnum.PENDING_RESPONSE;
    }

    return ConnectionStatusCategoryEnum.COMPLETED;
  }, [notifications?.isLoading, notifications?.notifications, userIsProvider]);

  const [statusCategoryFilter, setStatusCategoryFilter] =
    useSearchParamsState<ConnectionStatusCategoryEnum>(
      'statusCategory',
      initialStatusCategory,
      false
    );

  const { filters, setFilters } = useConnectionRequestsFilters();

  const breakpoint = useBreakpoint();

  const showOnlyTails = useMemo(() => !includes(breakpoint, SIZE_LG), [breakpoint]);
  const isShowGrid = useMemo(() => isGridView && !showOnlyTails, [isGridView, showOnlyTails]);

  const { progress, isProfileComplete, lastStep } = useUserProgress(
    user?.provider_completed_registration_steps
  );
  const { ConfirmationModal, showConfirmationModal } = useConfirmationModal();
  const { ConfirmationModal: CancelConfirmation, showConfirmationModal: showCancelConfirmation } =
    useConfirmationModal();
  const { FillQuestionnaireDialog, showFillQuestionnaireDialog } = useFillQuestionnaireDialog();
  const LIMIT = 25;
  const offset = (parseInt(page) - 1) * LIMIT;
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const queryParams = useMemo<
    Parameters<typeof ConnectedFacilityService.connectedFacilityList>[0]
  >(() => {
    const params = {
      limit: LIMIT,
      offset,
      ...filters,
      statusCategory: statusCategoryFilter,
    } as Parameters<typeof ConnectedFacilityService.connectedFacilityList>[0];

    if (!params.facility || params.facility.length === 0) {
      delete params.facility;
    }

    if (!params.opportunity || params.opportunity.length === 0) {
      delete params.opportunity;
    }

    if (user) {
      params.provider = [user.provider_id];
    }
    return params;
  }, [filters, offset, statusCategoryFilter, user]);

  const {
    data: connectionRequests,
    isLoading: isConnectionRequestsLoading,
    error: connectionRequestsFetchError,
  } = useQuery({
    queryKey: connectionRequestsQueryKeys.list(queryParams),
    queryFn: async () => {
      return ConnectedFacilityService.connectedFacilityList(queryParams);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(userQueryKeys.notifications());
    },
  });

  const mutationAcceptConnection = useMutation({
    mutationFn: async ({ connectedFacility }: { connectedFacility: string }) => {
      return ConnectedFacilityService.connectedFacilityAcceptPartialUpdate({
        id: connectedFacility,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(connectionRequestsQueryKeys.allLists());
      queryClient.invalidateQueries(userQueryKeys.notifications());
      queryClient.invalidateQueries(healthcareEntityFacilityQueryKeys.allLists());
    },
  });

  const mutationRejectConnection = useMutation({
    mutationFn: async ({ connectedFacility }: { connectedFacility: string }) => {
      return ConnectedFacilityService.connectedFacilityRejectPartialUpdate({
        id: connectedFacility,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(connectionRequestsQueryKeys.allLists());
      queryClient.invalidateQueries(userQueryKeys.notifications());
      queryClient.invalidateQueries(healthcareEntityFacilityQueryKeys.allLists());
    },
  });

  const mutationCancelConnection = useMutation({
    mutationFn: async ({ connectedFacility }: { connectedFacility: string }) => {
      return ConnectedFacilityService.connectedFacilityCancelPartialUpdate({
        id: connectedFacility,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(connectionRequestsQueryKeys.allLists());
      queryClient.invalidateQueries(userQueryKeys.notifications());
      queryClient.invalidateQueries(healthcareEntityFacilityQueryKeys.allLists());
    },
  });

  const submitAcceptedRequest = async (connectedFacility: ConnectedFacility) => {
    if (userIsProvider && !user?.completed_questionnaire) {
      const shouldNavigate = await showFillQuestionnaireDialog();
      if (shouldNavigate) {
        navigate(
          `/app/provider/questionnaire/${user?.provider_id}?next=/app/provider/connection-requests`
        );
        return;
      }
    }

    const accepted = await showConfirmationModal();
    if (!accepted) {
      return;
    }

    try {
      await mutationAcceptConnection.mutateAsync({
        connectedFacility: connectedFacility.id,
      });
      toast.success('This connection request is complete.');

      setShowReviewAvailabilityConfirmation(true);

      setReviewedConnectionRequest(null);
    } catch (error) {
      handleApiError(error);
    }
  };

  const onAccept = async (connectionRequest: ConnectedFacility) => {
    await submitAcceptedRequest(connectionRequest);
  };

  const submitRejectedRequest = async (connectedFacilityId: string) => {
    const rejected = await showConfirmationModal();
    if (!rejected) {
      return;
    }

    try {
      await mutationRejectConnection.mutateAsync({
        connectedFacility: connectedFacilityId,
      });
      toast.success('This connection request has been rejected.');

      setReviewedConnectionRequest(null);
    } catch (error) {
      handleApiError(error, { messagePrefix: 'Error rejecting connection request' });
    }
  };

  const onRowReject = (data: ConnectedFacility) => {
    submitRejectedRequest(data.id);
  };

  const submitCanceledRequest = async (connectedFacilityId: string) => {
    const canceled = await showCancelConfirmation();
    if (!canceled) {
      return;
    }

    try {
      await mutationCancelConnection.mutateAsync({
        connectedFacility: connectedFacilityId,
      });
      toast.success('This connection request has been canceled.');

      setReviewedConnectionRequest(null);
    } catch (error) {
      handleApiError(error, {
        messagePrefix: 'Error canceling connection request',
      });
    }
  };

  const onRowCancel = (data: ConnectedFacility) => {
    submitCanceledRequest(data.id);
  };

  useEffect(() => {
    // Only run it once to make the pending page the default one
    // when getting here directly from the link
    if (!statusCategoryFilter) {
      setStatusCategoryFilter(ConnectionStatusCategoryEnum.COMPLETED);
      // Only set default filters if no filters exist
      if (Object.keys(filters).length === 0 || isEqual(filters, {})) {
        setFilters(defaultFilter);
      }
    }
  }, []);

  const onCloseReviewAvailability = (result: boolean) => {
    setShowReviewAvailabilityConfirmation(false);

    if (result) {
      navigate('/app/provider/profile');
    }
  };

  const closeHandleConnectionDialog = (result: boolean) => {
    if (!reviewedConnectionRequest) return;

    if (result) {
      onAccept(reviewedConnectionRequest);
    } else {
      // Only set to null if user closes dialog without accepting
      setReviewedConnectionRequest(null);
    }
  };

  const updateSort = (data: SortDataPayload) => {
    setFilters({
      ...filters,
      sortBy: data.key,
      sortDirection: data.direction,
      ordering: data.direction === 'desc' ? `-${data.key}` : data.key,
    });
  };

  const facilitiesOptions = useMemo(() => {
    return connectionRequests?.results.map((connection) => connection.facility);
  }, [connectionRequests?.results]);

  const { data: opportunitiesData } = useQuery({
    queryKey: ['assignments-opportunities', 'active'],
    queryFn: async () => {
      return await AssignmentsService.assignmentsOpportunitiesList({
        showActive: true,
      });
    },
  });

  const opportunitiesOptions = useMemo(() => {
    return opportunitiesData?.results || [];
  }, [opportunitiesData?.results]);

  const searchOptions = useMemo(() => {
    if (!connectionRequests?.results) {
      return [];
    }

    return userIsProvider
      ? connectionRequests?.results.map((entity) => entity.healthcare_entity.name)
      : connectionRequests?.results.map((entity) => entity.provider.display_name);
  }, [userIsProvider, connectionRequests?.results]);

  const searchPlaceholder = useMemo(() => {
    return getConnectionsPageSearchPlaceholder(user?.user_type === UserTypeEnum.PROVIDER);
  }, [user?.user_type]);

  const handleContractRetrieveClick = async () => {
    if (!user?.provider_id) return;

    setIsLoadingContract(true);
    setShowContractModal(true);

    try {
      const xTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await ProvidersService.providersContractRetrieve({
        id: user.provider_id,
        xTz,
      });
      setContractText(response.text);
    } catch (error) {
      handleApiError(error, {
        messageOverride: 'Failed to load the working agreement. Please try again later.',
      });
      setShowContractModal(false);
    } finally {
      setIsLoadingContract(false);
    }
  };

  const handleDownloadContract = async () => {
    if (!user?.provider_id) return;

    try {
      setIsDownloadingContract(true);
      const xTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // Use native fetch to ensure we get a raw Blob, bypassing generated client parsing
      const response = await fetch(
        `${OpenAPI.BASE}/api/providers/${user?.provider_id}/contract-review-download/`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-tz': xTz,
          },
        }
      );

      if (!response.ok) {
        console.error('Download failed:', response.statusText);
        return;
      }

      // Explicitly get the blob from the response
      const blob = await response.blob();

      // Create the URL and download the link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `unexecuted_sendit_professional_services_agreement.pdf`);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      setIsDownloadingContract(false);
    } catch (error) {
      console.error('Failed to download PDF', error);
    }
  };

  const handleContractAgree = async () => {
    if (!user?.provider_id) return;

    setIsSigningContract(true);
    try {
      await ProvidersService.providersContractCreate({
        id: user.provider_id,
        xTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        requestBody: {
          signed_first_name: signedFirstName,
          signed_last_name: signedLastName,
        },
      });
      setShowContractModal(false);
      toast.success('Professional Services Agreement signed successfully.');
      queryClient.invalidateQueries({ queryKey: ['user', 'detail', 'current'] });
    } catch (error) {
      handleApiError(error, {
        messageOverride:
          'Failed to sign the Professional Services Agreement. Please try again later.',
      });
    } finally {
      setIsSigningContract(false);
    }
  };

  return (
    <>
      <div className="bg-primary-dark">
        <Container className="page-header-title">
          <h1>Facility Connections</h1>
        </Container>
        <Container>
          <TabsNavbar
            variant="primary-dark"
            tabs={[
              {
                itemID: 'completed',
                onClick: () => {
                  setPage('1');
                  setStatusCategoryFilter(ConnectionStatusCategoryEnum.COMPLETED);
                },
                label: 'Completed',
                active: isEqual(statusCategoryFilter, ConnectionStatusCategoryEnum.COMPLETED),
              },
              {
                itemID: 'in_process',
                onClick: () => {
                  setPage('1');
                  setStatusCategoryFilter(ConnectionStatusCategoryEnum.IN_PROCESS);
                },
                label: (
                  <span>
                    In Process <PendingPresentationRequestsBadge />
                  </span>
                ),
                active: isEqual(statusCategoryFilter, ConnectionStatusCategoryEnum.IN_PROCESS),
              },
              {
                itemID: 'pending',
                onClick: () => {
                  setPage('1');
                  setStatusCategoryFilter(ConnectionStatusCategoryEnum.PENDING_RESPONSE);
                },
                label: (
                  <span>
                    Pending <PendingConnectionRequestsBadge />
                  </span>
                ),
                active: isEqual(
                  statusCategoryFilter,
                  ConnectionStatusCategoryEnum.PENDING_RESPONSE
                ),
              },
              {
                itemID: 'rejected',
                onClick: () => {
                  setPage('1');
                  setStatusCategoryFilter(ConnectionStatusCategoryEnum.REJECTED);
                },
                label: 'Rejected',
                active: isEqual(statusCategoryFilter, ConnectionStatusCategoryEnum.REJECTED),
              },
              {
                itemID: 'denied',
                onClick: () => {
                  setPage('1');
                  setStatusCategoryFilter(ConnectionStatusCategoryEnum.DENIED);
                },
                label: 'Denied',
                active: isEqual(statusCategoryFilter, ConnectionStatusCategoryEnum.DENIED),
              },
              {
                itemID: 'all-connections',
                onClick: () => {
                  setPage('1');
                  setStatusCategoryFilter(ConnectionStatusCategoryEnum.ALL);
                },
                label: 'All Connections',
                active: isEqual(statusCategoryFilter, ConnectionStatusCategoryEnum.ALL),
              },
            ]}
          />
        </Container>
      </div>
      <Container className="mt-4">
        {user?.needs_contract_signed && (
          <Alert variant="warning" className="mb-3">
            <Alert.Heading>Action Required</Alert.Heading>
            <p className="mb-0">
              Our records show that you either haven&apos;t signed the Professional Services
              Agreement or there&apos;s an updated version available. Please{' '}
              <button
                onClick={handleContractRetrieveClick}
                className="alert-link border-0 bg-transparent p-0"
                style={{ textDecoration: 'underline', cursor: 'pointer' }}
              >
                complete this step
              </button>{' '}
              to accept connections.
            </p>
          </Alert>
        )}
        <Card className="mb-3">
          <Row className="p-2">
            <Col lg="9" xl="10" xs="12">
              <ConnectionRequestsFilters
                searchPlaceholder={searchPlaceholder}
                searchOptions={isConnectionRequestsLoading ? [] : searchOptions}
                facilitiesOptions={isConnectionRequestsLoading ? [] : (facilitiesOptions as [])}
                opportunitiesOptions={opportunitiesOptions}
                filters={filters}
                onApply={(data) => {
                  setPage('1');
                  setFilters(data);
                }}
                isProvider={user?.user_type === UserTypeEnum.PROVIDER}
              />
            </Col>
            {!showOnlyTails && (
              <Col xs="auto" className="ms-auto align-self-center">
                <ButtonGroup className="primary-lite-bg-toggle">
                  <ToggleButton
                    type="radio"
                    className={BUTTON_CLASS_NAME}
                    style={BUTTON_STYLES}
                    value="1"
                    name="view_type_grid"
                    variant="outline-primary"
                    checked={isShowGrid}
                    onClick={() => setIsGridView(true)}
                    onKeyUp={(e) => {
                      if (e.key === ' ' || e.key === 'Enter') {
                        setIsGridView(true);
                      }
                    }}
                    size="sm"
                  >
                    <BsJustify size={17} />
                  </ToggleButton>
                  <ToggleButton
                    type="radio"
                    className={BUTTON_CLASS_NAME}
                    style={BUTTON_STYLES}
                    value="0"
                    name="view_type_tails"
                    variant="outline-primary"
                    checked={!isShowGrid}
                    onClick={() => setIsGridView(false)}
                    onKeyUp={(e) => {
                      if (e.key === ' ' || e.key === 'Enter') {
                        setIsGridView(false);
                      }
                    }}
                    size="sm"
                  >
                    <BsGrid3X3GapFill size={17} />
                  </ToggleButton>
                </ButtonGroup>
              </Col>
            )}
          </Row>
        </Card>
        {!isShowGrid && isConnectionRequestsLoading && (
          <Row>
            <Col xs={12} className="d-flex align-items-center justify-content-center">
              <Spinner
                aria-label="loading indicator"
                animation="grow"
                className="loading-spinner"
                variant="primary"
              />
            </Col>
          </Row>
        )}
        {!isShowGrid && !!connectionRequestsFetchError && (
          <Row>
            <Col>
              <Alert variant="danger" show={!!connectionRequestsFetchError}>
                <Alert.Heading>Something went wrong</Alert.Heading>
                <p>Please try again later. If the problem persists, please contact us.</p>
              </Alert>
            </Col>
          </Row>
        )}
        {!isShowGrid && !!connectionRequests?.results && connectionRequests?.results?.length > 0 ? (
          <Row>
            {connectionRequests?.results?.map((connectionRequest) => (
              <Col key={connectionRequest.id} xs={12} sm={12} md={6} lg={6} xl={4} className="pb-3">
                <ConnectionRequestCard
                  onAccept={onAccept}
                  onReject={setRejectedConnectionRequest}
                  onCancel={onRowCancel}
                  connectionRequest={connectionRequest}
                  needsContractSigned={user?.needs_contract_signed}
                  onOpenContractModal={handleContractRetrieveClick}
                />
              </Col>
            ))}
          </Row>
        ) : (
          !isShowGrid &&
          (userIsProvider ? (
            <ConnectionRequestsEmptyInfoMessage />
          ) : (
            <Row className="ms-2 text-muted">No Connections Found</Row>
          ))
        )}
        {!isShowGrid &&
          !!connectionRequests?.results &&
          connectionRequests?.results?.length === 0 &&
          (!userIsProvider || isProfileComplete) && <p />}
        {isShowGrid && (!userIsProvider || isProfileComplete) && (
          <ConnectionRequestsTable
            onUpdateSort={updateSort}
            filters={filters}
            onEntityCancel={onRowCancel}
            onAccept={onAccept}
            isProvider={user?.user_type === UserTypeEnum.PROVIDER}
            onRowClick={setReviewedConnectionRequest}
            totalCount={connectionRequests?.count ?? 0}
            pageSize={LIMIT}
            currentPage={parseInt(page)}
            handleChangePage={(page) => setPage(page.toString())}
            isLoading={isConnectionRequestsLoading}
            list={connectionRequests?.results}
            onReject={onRowReject}
            needsContractSigned={user?.needs_contract_signed}
            onOpenContractModal={handleContractRetrieveClick}
          />
        )}
        {isShowGrid &&
          userIsProvider &&
          isProfileComplete &&
          !!connectionRequests?.results &&
          connectionRequests?.results?.length === 0 && <ConnectionRequestsEmptyInfoMessage />}
        {!isConnectionRequestsLoading &&
          !isProfileComplete &&
          user?.user_type === UserTypeEnum.PROVIDER && (
            <Row className="align-items-center justify-content-center">
              <Col lg="4">
                <ProfileIncompleteCard progress={progress} />
              </Col>
            </Row>
          )}

        {!isShowGrid && (
          <ListFooter
            title="Connection"
            totalCount={connectionRequests?.count ?? 0}
            pageSize={LIMIT}
            currentPage={parseInt(page)}
            onChangePage={(page: number) => setPage(page.toString())}
            containerClassName="py-2 px-2 text-muted"
          />
        )}
      </Container>
      <ReviewAvailability
        show={showReviewAvailabilityConfirmation}
        onHide={onCloseReviewAvailability}
      />
      <FillQuestionnaireDialog />
      {!!reviewedConnectionRequest && (
        <ReviewConnectionDialog
          onSubmit={submitAcceptedRequest}
          connectionRequest={reviewedConnectionRequest as ConnectedFacility}
          show={!!reviewedConnectionRequest}
          closeDialog={closeHandleConnectionDialog}
          onAccept={(connectionRequest) => {
            submitAcceptedRequest(connectionRequest);
          }}
          onReject={(connectionRequest) => {
            setRejectedConnectionRequest(connectionRequest);
          }}
          onCancel={(connectionRequest) => {
            onRowCancel(connectionRequest);
          }}
          needsContractSigned={user?.needs_contract_signed}
          onOpenContractModal={handleContractRetrieveClick}
        />
      )}
      {!!rejectedConnectionRequest && (
        <RejectConnectionDialog
          connectionRequest={rejectedConnectionRequest}
          show={!!rejectedConnectionRequest}
          closeDialog={() => setRejectedConnectionRequest(null)}
          onRejectSuccess={() => setReviewedConnectionRequest(null)}
        />
      )}
      <ConfirmationModal
        title="Connection Agreement"
        htmlText={
          <>
            <h5 className="text-section-name--dark-brand mb-2">
              {reviewedConnectionRequest?.facility.name}
            </h5>
            <div className="mt-2">
              <p>
                Accepting the connection indicates you are interested in sharing your contact
                information and discussing the opportunity further but does not guarantee commitment
                by either party.
              </p>
              <p>Next steps will include:</p>
              <ul style={{ listStyleType: 'disc' }}>
                <li style={{ paddingBottom: '10px' }}>
                  The organization verifying that you do not currently have a relationship
                </li>
                <li style={{ paddingBottom: '10px' }}>
                  The facility reviewing your profile for approval
                </li>
                <li style={{ paddingBottom: '10px' }}>
                  A call with the Clinical Leader where you can ask questions and share what makes
                  you a great fit
                </li>
              </ul>
            </div>
            <div className="">
              <p>
                By accepting you <b>acknowledge and authorize</b>
              </p>
              <ul style={{ listStyleType: 'disc' }}>
                <li style={{ paddingBottom: '10px' }}>Initial name clearance process</li>
                <li style={{ paddingBottom: '10px' }}>
                  Facility presentation (once clearance received)
                </li>
                <li style={{ paddingBottom: '10px' }}>
                  Any shifts worked as a result of this connection will be compensated at the rates
                  listed
                </li>
              </ul>
            </div>
          </>
        }
      />
      <CancelConfirmation
        confirmText="Cancel"
        cancelVariant="outline-primary"
        cancelText="Exit"
        confirmVariant="danger"
        title="Cancel Connection Request"
        text="Are you sure you want to cancel this connection request?"
      />
      <FacilityCellDetailsPortal />

      <Modal show={showContractModal} onHide={() => setShowContractModal(false)} size="lg">
        <Modal.Header closeButton>
          <div>
            <Modal.Title>Professional Services Agreement</Modal.Title>
            <div className="text-muted small mt-1">
              {isDownloadingContract ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Downloading...
                </>
              ) : (
                <>
                  Please review and sign at the bottom of the page.{' '}
                  <button
                    onClick={handleDownloadContract}
                    className="border-0 bg-transparent p-0 text-decoration-underline text-color-primary"
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="fw-medium"> Click here to download </span>
                  </button>{' '}
                  and review.
                  <i className="bi bi-download ms-1" />
                </>
              )}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          {isLoadingContract ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : (
            <>
              <div className="tw:px-2" dangerouslySetInnerHTML={{ __html: contractText || '' }} />
              <div className="mt-4">
                <p className="mb-3 text-danger">
                  Please enter your name to sign and accept this agreement
                </p>
                <Form>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Control
                          type="text"
                          value={signedFirstName}
                          onChange={(e) => setSignedFirstName(e.target.value)}
                          placeholder="First Name"
                          required
                          disabled={isSigningContract}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Control
                          type="text"
                          value={signedLastName}
                          onChange={(e) => setSignedLastName(e.target.value)}
                          placeholder="Last Name"
                          required
                          disabled={isSigningContract}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Form>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={() => setShowContractModal(false)}
            disabled={isSigningContract}
            tabIndex={-1}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleContractAgree}
            disabled={!signedFirstName || !signedLastName || isSigningContract}
          >
            {isSigningContract ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Signing...
              </>
            ) : (
              'Sign'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
